// Org-scoped query module — owns the mechanism every org-scoped call site used
// to repeat inline: injecting orgId into the cache key, `enabled:
// !!activeOrgId`, injecting `orgId` into apiFetch, poll-until-terminal cadence,
// and org-scoped mutations. Call sites keep writing the fetcher itself (plain
// apiFetch, same envelope peeling as before); polling predicates come from
// @/lib/states (C1), intervals from @/lib/constants.
//
// Imports only from @/stores/org + @tanstack/react-query (org already imports
// @/lib/api) — no import cycles.
import { useQuery, useMutation } from '@tanstack/react-query'
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query'
import { useOrg } from '@/stores/org'

/**
 * Cache-key contract — one dialect only. Every org-scoped key is
 * `[domain, orgId, ...leaf/params]` built by a resource-owned factory in
 * @/lib/resources/* . A factory is `(orgId) => tuple`, optionally curried over
 * params first: `modelsKeys.list` is used as-is, `modelsKeys.detail(id)`
 * returns the `(orgId) => tuple` builder. orgId sits at index 1 always, so
 * `xxxKeys.all(orgId)` = `[domain, orgId]` prefix-invalidates the whole domain
 * for that org — never another org's entries, never a sibling root left stale.
 * `orgId` is `string | null` because keys are computed before an org exists;
 * the query stays disabled until then, and a null-org prefix matches nothing.
 */
export type OrgQueryKey<K extends readonly unknown[] = readonly unknown[]> = (
  orgId: string | null,
) => K

export type OrgQueryOptions<T, K extends readonly unknown[] = readonly unknown[]> = Omit<
  UseQueryOptions<T, Error, T, K>,
  'queryKey' | 'queryFn' | 'enabled' | 'refetchInterval'
> & {
  /** ANDed with `!!activeOrgId`. */
  enabled?: boolean
  /** Poll (at `pollMs`) while this returns true; receives undefined when no data yet. */
  pollWhile?: (data: T | undefined) => boolean
  /** Polling interval, from @/lib/constants. */
  pollMs?: number
}

/**
 * Org-scoped useQuery. `queryKey` is a resource key factory resolving to
 * `[domain, orgId, ...]` — the org slot is fixed at index 1 by contract, so
 * invalidation goes through the same factories (`xxxKeys.all(orgId)` for the
 * whole domain, or a leaf factory for a narrower prefix). When disabled (no
 * active org, or opts.enabled false) nothing fetches; if the org is somehow
 * missing when the query runs, queryFn throws a plain Error('no active org').
 * `pollWhile` + `pollMs` replace hand-rolled refetchInterval callbacks; a bare
 * `pollMs` (no pollWhile) polls at a fixed cadence.
 */
export function useOrgQuery<T, K extends readonly unknown[] = readonly unknown[]>(
  queryKey: OrgQueryKey<K>,
  fetcher: (orgId: string) => Promise<T>,
  opts?: OrgQueryOptions<T, K>,
) {
  const activeOrgId = useOrg((s) => s.activeOrgId)
  const pollWhile = opts?.pollWhile
  const pollMs = opts?.pollMs
  return useQuery<T, Error, T, K>({
    ...opts,
    queryKey: queryKey(activeOrgId),
    queryFn: () => {
      if (!activeOrgId) throw new Error('no active org')
      return fetcher(activeOrgId)
    },
    enabled: !!activeOrgId && (opts?.enabled ?? true),
    refetchInterval: pollWhile
      ? (q) => (pollWhile(q.state.data) ? pollMs ?? false : false)
      : pollMs,
  })
}

/**
 * Org-scoped useMutation: injects the active org as the first mutationFn
 * argument (mutation body/params keep their current wire shape). If no org is
 * active the mutation throws Error('no active org') — every existing call site
 * already guards rendering on `!!activeOrgId`, so this matches today's
 * behavior. TVars defaults to void for zero-argument mutations.
 */
export function useOrgMutation<TVars = void, TData = unknown>(
  mutationFn: (orgId: string, vars: TVars) => Promise<TData>,
  opts?: Omit<UseMutationOptions<TData, Error, TVars, unknown>, 'mutationFn'>,
) {
  const activeOrgId = useOrg((s) => s.activeOrgId)
  return useMutation<TData, Error, TVars, unknown>({
    ...opts,
    mutationFn: (vars: TVars) => {
      if (!activeOrgId) throw new Error('no active org')
      return mutationFn(activeOrgId, vars)
    },
  })
}
